import React from "react"
import {connect} from "react-redux"
import {bindActionCreators} from "redux"
import loadable from "@loadable/component"

import {AgDetailsTable} from "./ui/AgDetailsTable"
import TitleBar from "./ui/TitleBar"
import {genericStringPairProperties, GridUtil} from "../utils/GridUtil"
import {
  displaySprintPayOption,
  iodtLocalToDateOnlyForPickup,
  iodtUtcToDateOnlyForLfd,
  iodtUtcToTimezoneDateTime24Hr,
  merge
} from "../utils/util"
import {navigateToPayCargoToFinishPayment} from "../state/todolist/actions"
import TotalChargeAndInvoiceRenderer from "./ui/TotalChargeAndInvoiceRenderer"
import InvoiceAndStatusRenderer from "./ui/InvoiceAndStatusRenderer"
import DateWithIconColumnRenderer from "./ui/DateWithIconColumnRenderer"
import {navigateToPayCharges, navigateToProcessStart} from "../state/dnd/actions"
import NotifyDialog from "./ui/NotifyDialog"
import ToDoListSearch from "./ui/ToDoListSearch"
import MaintenancePage from "./ui/MaintenancePage"
import {getMaintDates} from "../state/app/actions"
import SupportedTerminals from "./ui/SupportedTerminals"
import DndChargesRenderer from "./ui/DndChargesRenderer"
import IfYouHaveAnyQuestions from "./IfYouHaveAnyQuestions"

const pair = {agColumnProperties: genericStringPairProperties}

const AgGridReact = loadable(() => import(/* webpackChunkName: "agGrid" */ "../splitting/agGridSetup"))

const allColumns = {
  checkBox: {
    headerName: '',
    columnStyles: ['thinRow'],
    checkboxSelection: true,
    columnWidth: 20,
    suppressMenu: true,
    suppressSizeToFit: true
  },
  cntrNumPair: {
    headerName: 'Container #',
    columnWidth: 125,
    suppressSizeToFit: true,
    ...pair
  },
  cntrStatusPair: {
    headerName: 'Status',
    columnWidth: 130,
    suppressSizeToFit: true,
    ...pair
  },
  releaseStatusPair: {
    headerName: 'Release Status',
    columnWidth: 140,
    suppressSizeToFit: true,
    ...pair
  },
  lastFreeDate: {
    headerName: 'Demurrage Last Free Date',
    columnWidth: 124,
    agColumnProperties: {cellRendererFramework: DateWithIconColumnRenderer},
    suppressSizeToFit: true
  },
  latestPickupDate: {
    headerName: 'Target Pick up Date',
    columnWidth: 105,
    agColumnProperties: {cellRendererFramework: DateWithIconColumnRenderer},
    suppressSizeToFit: true
  },
  demCharge: {
    headerName: 'Demurrage Charges',
    columnWidth: 125,
    agColumnProperties: {cellRendererFramework: DndChargesRenderer},
    suppressSizeToFit: true
  },
  rmcCharge: {
    headerName: 'Reefer Monitoring Charges',
    columnWidth: 150,
    agColumnProperties: {cellRendererFramework: DndChargesRenderer},
    suppressSizeToFit: true
  },
  totalCharges: {
    headerName: 'Total Charges',
    columnWidth: 145,
    agColumnProperties: {cellRendererFramework: TotalChargeAndInvoiceRenderer},
    suppressSizeToFit: true
  },
  invoiceDetails: {
    headerName: 'Invoice # & Status',
    columnWidth: 579,
    agColumnProperties: {cellRendererFramework: InvoiceAndStatusRenderer},
    suppressSizeToFit: true
  },
  spacer: {
    headerName: '',
    columnWidth: 0
  }
}

const SHOW_LFD_CNTR_STATUSES = ['On Train', 'Discharged', 'At Location', 'Out for Delivery', 'Empty Returned']

export class ToDoList extends React.Component {
  constructor(props) {
    super(props)
    this.gridUtil = null
    AgGridReact.load().then(() => {
      this.setState({gridLoading: false})
    })
    let state = {
      disableDndInquire: true,
      disablePayCharges: true,
      gridLoading: true,
      transformedRows: [],
      title: 'Real Time Release'
    }
    if (props.data && props.data.length) {
      state = merge(state, this.transformRowsAndGetUpdatedState(props.data))
    }
    this.state = state
  }

  getSupportedFacilities() {
    const env = this.props.userProfile?.env
    const facilities = this.props.userProfile?.forCosco
      // COSCO
        ? ['HOU02', 'HOU05', 'NYC01', 'NYC05', 'NYC07', 'NYC08', 'BAL01', 'MIA03', 'MOB01', 'OAK03', 'OAK05',
                  'TIW04', 'SEA01', 'SEA09', 'SEA40', 'BOS01', 'CHS03', 'CHS04', 'MSY04', 'OAK07',
                  'ORF01', 'ORF03', 'PEF01', 'PHL01', 'SAV01', 'TPA01', 'CDL31', 'RIC12',
                   'GXX31', 'FRR01',  'DLL31', 'JAX34', 'GYV31', 'JAX02']

      // OOCL
        : ['NYC02', 'NYC07', 'NYC11', 'NYC14', 'LGB10', 'LGB23', 'LAS02', 'LAS03', 'LAS04', 'LAS08', 'LAS09',
          'ORF04', 'ORF08', 'OAK28', 'OAK01', 'OAK08',
          'TCM09', 'SAV03', 'CHS01', 'CHS03', 'CHS05', 'SEA04', 'SEA10', 'SEA24', 'MIA03', 'MOB02', 'TPA06',
          'HOU05', 'HOU07', 'NEW02', 'BOS07', 'BAL01', 'PHX10']
    if ('PRD' !== env && this.props.userProfile?.forCosco) {
      // apibot for COSCO is setup to create BL for LGB08
      facilities.push('LGB08')
    }
    return facilities
  }

  componentDidMount() {
    this.props.actions.getMaintDates()
  }

  componentDidUpdate(prevProps) {
    const newData = this.props.data
    if (prevProps.data !== newData) {
      if (this.gridUtil) {
        if (!newData) {
          // setRowData([]) is needed here b/c when data goes to null, the grid is not rendered but
          // the grid is still in memory? and holds on to the old data.  This causes issues later
          // when new data comes in and the grid is rendered again (see related bug for this commit)
          this.gridUtil.gridApi.setRowData([])
        } else {
          this.gridUtil.gridApi.deselectAll()
        }
      }
      this.setState(this.transformRowsAndGetUpdatedState(newData))
      if (newData?.length === 1) {
        this.needsAutoSelected = true
      }
    }
    if (prevProps.searching !== this.props.searching) {
      this.handleRowSelected()
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const {data} = nextProps
    let title = 'Real Time Release'
    if (data && data.length > 0) {
      title = 'Real Time Release > Import Demurrage Charge'
    }
    if (title !== prevState.title) {
      return {
        title: title
      }
    }
    return null
  }

  hasSomeSelectableRow() {
    return this.props.data && this.props.data.some(ea => ea.canEdit)
  }

  transformRowsAndGetUpdatedState(data) {
    const transformedRows = this.getTransformedRows(data)
    return {
      transformedRows: transformedRows,
      showUnsupportedLhubDialog: !!transformedRows.some(ea => ea.unsupportedLhub),
      disableDndInquire: true,
      disablePayCharges: true,
      hasOutstandingPayment: false
    }
  }

  getTransformedRows(data) {
    if (!data) {
      return []
    }

    return data.map((row) => {
      const statusDate = iodtUtcToTimezoneDateTime24Hr(row.statusDateUtc, row.pickupTz, false)

      const isInvoiced = !!row.invoices.length
      let relDateUtc
      let releaseStatus

      if (row.relStatus === 'Held') {
        releaseStatus = row.relStatus
        relDateUtc = row.relCategory
      } else {
        releaseStatus = row.relStatus
        relDateUtc = iodtUtcToTimezoneDateTime24Hr(row.relDateUtc, row.pickupTz, false)
      }

      const showLFD = SHOW_LFD_CNTR_STATUSES.includes(row.cntrStatus)
      row.cntrNumPair = {
        first: row.checkDigit ? row.cntrNum + '-' + row.checkDigit : row.cntrNum,
        second: row.sizeType
      }
      row.cntrStatusPair = {first: row.cntrStatus, second: statusDate}
      row.releaseStatusPair = {first: releaseStatus, second: relDateUtc}
      row.lastFreeDate = showLFD && {
        relativeDate: iodtUtcToDateOnlyForLfd(row.lfdUtc, row.pickupTz, true),
        relativeDateName: 'Last Free Date'
      }
      if (row.rmcLfdUtc) {
        row.lastRmcFreeDt = iodtUtcToDateOnlyForLfd(row.rmcLfdUtc, row.pickupTz, true)
      }
      row.latestPickupDate = {
        relativeDate: iodtLocalToDateOnlyForPickup(row.pickupDate, row.pickupTz, false),
        relativeDateName: 'Target Pick Up Date'
      }
      row.invoiceDetails = isInvoiced
          ? row.invoices.map((invoice) => {
            return {...invoice, currency: row.currency}
          })
          : []
      row.unsupportedLhub = !(this.getSupportedFacilities().includes(row.lhubFcil))
      if (row.unsupportedLhub) {
        row.canEdit = false
      }
      return row
    })
  }

  myOnGridMount(agGrid) {
    if (agGrid) {
      this.gridUtil = new GridUtil(agGrid)
      agGrid.api.sizeColumnsToFit()
      if (this.props.onGridMount) {
        this.props.onGridMount(agGrid)
      }
      if (this.needsAutoSelected && this.gridUtil.getAllRowNodes().length === 1) {
        this.gridUtil.getAllRowNodes()[0].setSelected(true)
        this.needsAutoSelected = false
      }
    }
  }

  onReady(agGrid) {
    this.gridUtil = new GridUtil(agGrid)
    window.onresize = this.handleWindowResize.bind(this)
  }

  handleWindowResize() {
    this.gridUtil && this.gridUtil.gridApi.sizeColumnsToFit()
  }

  getRowHeight(params) {
    const maxLines = params.data.invoiceDetails ? params.data.invoiceDetails.length : 1
    if (maxLines > 2) {
      // 17.14 is the height of each row, 20 is 2x10px padding top & bottom
      return (maxLines * 17.14) + 20
    }
    return 50
  }

  handleDnDClick() {
    const relatedCntrs = this.getRelatedRowNodesForSelectedInvoices().map(ea => ea.data)
    if (this.passesCheckForMissingRequiredInvoice(relatedCntrs)) {
      this.props.actions.navigateToProcessStart(relatedCntrs)
    }
  }

  handlePayCharges(platform) {
    this.selectAllRowsForSelectedInvoices()
    let selectedRelatedNodes = this.gridUtil.gridApi.getSelectedNodes()
    let hasOutstandingPayment = false
    selectedRelatedNodes.forEach(ea => {
      if (!ea.data.totalCharges) {
        ea.setSelected(false)
      } else {
        hasOutstandingPayment = hasOutstandingPayment || ea.data.paymentStatus === 'Not Available - Payment Requested'
      }
    })
    selectedRelatedNodes = this.gridUtil.gridApi.getSelectedNodes()
    const osPaymentReqNos = new Set()
    this.gatherAllOutstandingPaymentReqNos(selectedRelatedNodes, osPaymentReqNos)
    if (osPaymentReqNos.size > 1) {
      return
    }

    // osPaymentReqNos is expected to be non-None only for non-popup PC payments created and still
    // waiting to be paid on PC platform
    if (osPaymentReqNos.size === 1 && !this.props.userProfile?.usePcPopup) {
      const osPaymentReqNo = osPaymentReqNos.values().next().value
      this.props.actions.navigateToPayCargoToFinishPayment(
          osPaymentReqNo, this.state.transformedRows[0].lhubFcil)
      return
    }
    const relatedCntrs = selectedRelatedNodes.map(ea => ea.data)
    if (this.passesCheckForMissingRequiredInvoice(relatedCntrs)) {
      this.props.actions.navigateToPayCharges(relatedCntrs, platform)
    }
  }

  getFirstOustandingRequestNumberForSelectedNodes() {
    let paymentReqNo = null
    this.gridUtil.gridApi.getSelectedNodes().forEach((ea) => {
      ea.data.invoices.forEach((inv) => {
        if ((inv.status === 'Outstanding' || inv.status === 'Processing') && inv.paymentReqNo) {
          paymentReqNo = inv.paymentReqNo
        }
      })
    })
    return paymentReqNo
  }

  gatherAllOutstandingPaymentReqNos(rowNodes, set) {
    rowNodes.forEach((ea) => {
      if (ea.data.paymentStatus === 'Not Available - Payment Requested') {
        ea.data.invoices.forEach((inv) => {
          if (['Outstanding','Processing'].includes(inv.status) && inv.paymentReqNo) {
            set.add(inv.paymentReqNo)
          }
        })
      }
    })
  }

  passesCheckForMissingRequiredInvoice(relatedCntrs) {
    const hasSomeTbdRow = relatedCntrs.some(eaCntr => eaCntr.invoices.some(inv => 'TBD' === inv.invoice))
    if (hasSomeTbdRow) {
      this.setState({showRequiredInvoiceMissing: true})
      return false
    }
    return true
  }

  selectAllRowsForSelectedInvoices() {
    this.getRelatedRowNodesForSelectedInvoices().forEach(ea => ea.setSelected(true))
  }

  getRelatedRowNodesForSelectedInvoices() {
    const gatherArray = []
    if (this.gridUtil) {
      this.gridUtil.gridApi.getSelectedNodes().forEach((row) => {
        gatherArray.push(row)
      })
      this.gatherRelatedRowNodesForSharedInvoices(gatherArray)
    }
    return gatherArray
  }

  gatherRelatedRowNodesForSharedInvoices(rowNodesToGather) {
    // Need to do recursively.  If a new container is pulled in having a new unseen invoice, then all
    // containers linked to that invoice also need to be pulled.
    const allRowNodes = this.gridUtil.getAllRowNodes()
    const initialSelectedInvoices = new Set()
    rowNodesToGather.forEach((rowNode) => {
      rowNode.data.invoices.forEach((invRow) => {
        initialSelectedInvoices.add(invRow.invoice)
      })
    })
    const finalSelectedInvoices = new Set()
    allRowNodes.forEach((rowNode) => {
      const invoiceIsSelected = rowNode.data.invoices.some(
          (invRow) => initialSelectedInvoices.has(invRow.invoice))
      if (invoiceIsSelected) {
        if (!rowNodesToGather.includes(rowNode)) {
          rowNodesToGather.push(rowNode)
        }
        rowNode.data.invoices.forEach((invRow) => finalSelectedInvoices.add(invRow.invoice))
      }
    })
    if (initialSelectedInvoices.size !== finalSelectedInvoices.size) {
      this.gatherRelatedRowNodesForSharedInvoices(rowNodesToGather)
    }
  }

  handleRowSelected() {
    if (!this.gridUtil) {
      return
    }
    const relatedRowNodes = this.getRelatedRowNodesForSelectedInvoices()

    const hasRows = relatedRowNodes.length
    const isSearching = this.props.searching
    let hasOutstandingInvoice = false
    let hasOutstandingPayment = false
    let canEditFromServer = false
    let lastHubIsSupported = true
    let hasPaymentInProgress = false
    let hasProcessingInvoice = false
    relatedRowNodes.forEach((rowNode) => {
      const rowData = rowNode.data
      if (rowData.invoices.some(invDtl => ['Outstanding'].includes(invDtl.status))) {
        hasOutstandingInvoice = true
      }
      if (rowData.canEdit) {
        canEditFromServer = true
      }
      if (rowData.unsupportedLhub) {
        lastHubIsSupported = false
      }
      if (rowNode.data.invoices.some(i => i.status === 'Processing')) {
        hasProcessingInvoice = true
      }
    })

    // Full block if a payment is started but not reconciled yet.  To be refined later
    // to allow multiple in progress payments as long as related containers for each payment
    // are unique (not shared across payments)
    this.gridUtil.getAllRowNodes().forEach((rowNode) => {
      const paymentStatus = rowNode.data.paymentStatus
      if (paymentStatus === 'Not Available - Payment Requested') {
        hasOutstandingPayment = true
      } else if (paymentStatus === 'Not Available - In Progress') {
        hasPaymentInProgress = true
      }
    })
    const osPaymentReqNos = new Set()
    this.gatherAllOutstandingPaymentReqNos(relatedRowNodes, osPaymentReqNos)
    const hasOneOrNoneOsPymnt = osPaymentReqNos.size < 2

    const allowInquire = !isSearching && hasRows && lastHubIsSupported && canEditFromServer
        && !hasPaymentInProgress
    const allowPayCharges = !isSearching && hasOutstandingInvoice && !hasPaymentInProgress
        && !hasProcessingInvoice && hasOneOrNoneOsPymnt

    this.setState({
      disableDndInquire: !allowInquire,
      disablePayCharges: !allowPayCharges,
      hasOutstandingPayment: hasOutstandingPayment
    })
  }

  getGridAndButtons(displayReefer) {
    const {data} = this.props
    if (!data || this.state.gridLoading) {
      return null
    }
    const nonGridHeight = 340 + (this.state.hasOutstandingPayment ? 33 : 0)

    const columns = {...allColumns}
    columns.checkBox.headerCheckboxSelection = this.hasSomeSelectableRow.bind(this)
    if (!displayReefer) {
      columns.totalCharges = merge(columns.totalCharges, {headerName: 'Total Demurrage Charges'})
      delete columns.demCharge
      delete columns.rmcCharge
    }
    return (
        <>
          <div id="spacer" style={{height: 10}}/>
          <AgDetailsTable
              columns={columns}
              rowsData={this.state.transformedRows}
              headerHeight={51}
              topMargin={nonGridHeight}
              gridAutoHeight={this.state.transformedRows.length < 4}
              enableMultiRowSelection={true}
              onGridMount={this.myOnGridMount.bind(this)}
              onGridReady={this.onReady.bind(this)}
              rowHeightFn={this.getRowHeight.bind(this)}
              onRowSelected={this.handleRowSelected.bind(this)}
              isRowSelectable={(rowNode) => rowNode.data.canEdit}
              rowClassRules={{
                'disabled-row': (params) => {
                  return !params.data.canEdit
                }
              }}
          />
          <div
              style={{
                fontSize: 14,
                paddingLeft: 20,
                paddingTop: 5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
            <IfYouHaveAnyQuestions userProfile={this.props.userProfile}/>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            paddingLeft: 20,
            paddingTop: 10
          }}>
            <button className="blue-button-round inquire-dnd"
                    onClick={this.handleDnDClick.bind(this)}
                    disabled={this.state.disableDndInquire}>Set or Revise Pick Up Date
            </button>
            <div style={{width: 20}}/>
            <button className="blue-button-round pay-charges"
                    onClick={this.handlePayCharges.bind(this, 'PC')}
                    disabled={this.state.disablePayCharges}>Complete Payment using PayCargo
            </button>
            <div style={{width: 20}}/>
            {displaySprintPayOption() &&
                <button className="blue-button-round pay-charges"
                        onClick={this.handlePayCharges.bind(this, 'SP')}
                        disabled={this.state.disablePayCharges}>Complete Payment using SprintPay
                </button>}
          </div>
          {this.state.showUnsupportedLhubDialog && <NotifyDialog
              titleText={'Service unavailable at pick up location!'}
              bodyText={<div>
                <span>At this time, we are only supporting payments for pick up at the following:</span>
                <SupportedTerminals
                    style={{fontWeight: 600, lineHeight: 1.5}}
                    userProfile={this.props.userProfile}/>
                <span>We are working to add support for additional locations and will make announcements as they become available.</span>
              </div>}
              dialogInsideModal={true}
              okCallback={() => {
                this.setState({showUnsupportedLhubDialog: false})
              }}
          />}
          {this.state.showRequiredInvoiceMissing && <NotifyDialog
              titleText={'System Processing Task'}
              bodyText={'Our system is currently processing your previous request. Please try again in a few minutes and refresh your search page.'}
              dialogInsideModal={true}
              okCallback={() => {
                this.setState({showRequiredInvoiceMissing: false})
              }}
          />}
        </>
    )
  }

  render() {
    const {messages, data} = this.props
    const firstRow = data?.[0] || {}
    const isLocked = firstRow.paymentStatus === "Locked"
        || data?.some(ea => ea.paymentStatus === 'Not Available - In Progress')
    if (messages && messages.length && messages[0] === "System Unavailable") {
      return (<MaintenancePage/>)
    } else {
      const forCosco = this.props?.userProfile?.forCosco
      const height = forCosco ? 'calc(100vh - 25px)' : '100vh'
      return (
          <div id="todo-list"
               style={{width: '100%', height: height, display: 'flex', flexDirection: 'column'}}>
            <TitleBar
                titleStyle={{height: 78}}
                showBackButton={false}
                title={this.state.title}
                showLogout={true}
            />
            <ToDoListSearch isLocked={isLocked}/>
            {this.getGridAndButtons(firstRow.cargoGroup === "RF" && !forCosco)}
          </div>
      )
    }
  }
}

function mapStateToProps(state) {
  return {
    userProfile: state.userProfile,
    data: state.todolist.data,
    messages: state.todolist.messages,
    searching: state.todolist.searching,
    voidAmount: state.payment.voidAmount,
    errorMessage: state.payment.errorMessage,
    dndRowsToShow: state.dnd.rowsToShow
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      navigateToProcessStart, navigateToPayCharges,
      navigateToPayCargoToFinishPayment, getMaintDates
    }, dispatch), dispatch
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ToDoList)